import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Shield, X } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { track } from '@/utils/analytics';

const ESSENTIAL_KEY = 'storage_consent_essential';
const FUNCTIONAL_KEY = 'storage_consent_functional';
const DISMISSED_KEY = 'storage_consent_dismissed';
const RECORDED_AT_KEY = 'storage_consent_recorded_at';

const CookieConsent: React.FC = () => {
  const [visible, setVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [functionalEnabled, setFunctionalEnabled] = useState(true);

  useEffect(() => {
    const dismissed = localStorage.getItem(DISMISSED_KEY);
    if (!dismissed) {
      setVisible(true);
    }
  }, []);

  const savePreferences = (functional: boolean) => {
    const recordedAt = new Date().toISOString();
    localStorage.setItem(ESSENTIAL_KEY, 'true');
    localStorage.setItem(FUNCTIONAL_KEY, functional ? 'true' : 'false');
    localStorage.setItem(DISMISSED_KEY, 'true');
    localStorage.setItem(RECORDED_AT_KEY, recordedAt);
    // Legacy key for backward compatibility
    localStorage.setItem('storage_consent', functional ? 'accepted' : 'essential_only');
    void track('cookie_consent_recorded', {
      essential: true,
      functional,
      recorded_at: recordedAt,
    });
    setVisible(false);
  };

  const handleAcceptAll = () => savePreferences(true);
  const handleRejectNonEssential = () => savePreferences(false);
  const handleSaveChoices = () => savePreferences(functionalEnabled);

  if (!visible) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 p-2 sm:p-4 pb-safe px-safe pointer-events-none">
      <div
        role="dialog"
        aria-label="Storage and privacy notice"
        className="pointer-events-auto max-w-4xl mx-auto bg-black/95 backdrop-blur-xl border border-white/20 rounded-xl shadow-2xl overflow-hidden"
      >
        {/* Compact top row: message + primary actions + dismiss */}
        <div className="flex items-start gap-3 p-3 sm:p-4">
          <Shield className="w-5 h-5 text-primary flex-shrink-0 mt-0.5 hidden sm:block" />
          <div className="flex-1 min-w-0 text-sm text-white/85">
            <p className="font-medium text-white sm:hidden mb-1">Storage & Privacy</p>
            <p className="leading-snug">
              We use browser storage for essential features and optional preferences. No third-party
              tracking. <a href="/privacy" className="text-primary hover:underline">Privacy Policy</a>.
            </p>
          </div>
          <button
            type="button"
            onClick={handleRejectNonEssential}
            aria-label="Reject non-essential and close"
            className="flex-shrink-0 -mt-1 -mr-1 h-11 w-11 sm:h-9 sm:w-9 inline-flex items-center justify-center rounded-md text-white/60 hover:text-white hover:bg-white/10"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Actions row — stacked and full width on phones, inline from sm up */}
        <div className="px-3 sm:px-4 pb-3 sm:pb-4 grid grid-cols-2 gap-2 sm:flex sm:flex-wrap sm:items-center sm:justify-end">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowDetails((v) => !v)}
            className="text-white/60 hover:text-white h-11 sm:h-9 w-full sm:w-auto"
          >
            {showDetails ? 'Hide options' : 'Customize'}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleRejectNonEssential}
            className="text-white/70 hover:text-white h-11 sm:h-9 w-full sm:w-auto"
          >
            Essential only
          </Button>
          <Button
            size="sm"
            onClick={handleAcceptAll}
            className="h-11 sm:h-9 col-span-2 w-full sm:w-auto"
          >
            Accept all
          </Button>
        </div>

        {/* Collapsible detail */}
        {showDetails && (
          <div className="border-t border-white/10 px-3 sm:px-4 py-3 space-y-2 bg-white/[0.02]">
            <label className="flex items-center gap-2 cursor-default">
              <Checkbox checked disabled className="opacity-70" />
              <span className="text-white text-sm font-medium">Essential</span>
              <span className="text-white/50 text-xs">— always on</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer" onClick={() => setFunctionalEnabled(!functionalEnabled)}>
              <Checkbox checked={functionalEnabled} onCheckedChange={(v) => setFunctionalEnabled(!!v)} />
              <span className="text-white text-sm font-medium">Functional</span>
              <span className="text-white/50 text-xs">— theme, consent memory</span>
            </label>
            <div className="pt-2 flex justify-end">
              <Button variant="outline" size="sm" onClick={handleSaveChoices} className="h-9">
                Save choices
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CookieConsent;
